// src/pages/SearchPage.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Rating,
  Divider,
  Chip,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { GlobalStyles } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { apiClient } from '../../publicApiClient';
import { trackSearch } from '../../utils/analytics';
import { SpaceSearchResult } from '../../models/Space';
import { SpaceRankBoost } from '../../models/SpaceRankBoost';
import BoltIcon from '@mui/icons-material/Bolt';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { generateSpaceUrlId, toUrlFriendly, fromUrlFriendlyCity } from '../../utils/urlUtils';
import { resolveMarketingCanonicalForAppPath } from '../../utils/marketingCanonical';
import { filterSpacesByNameQuery, isSpace24hOpen } from '../../utils/spaceUtils';
import { isUserInWarsaw } from '../../utils/locationUtils';
import { getAmenityLabel } from '../../amenities';
import SEO from '../../components/SEO';
import MediaDisplay from '../../components/MediaDisplay';
import SearchBar from '../../components/SearchBar';

import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';

import ViewListIcon from '@mui/icons-material/ViewList';
import MapIcon from '@mui/icons-material/Map';

// Custom marker component
interface PriceMarkerProps {
  price: string;
  name: string;
  position: {
    lat: number;
    lng: number;
  };
  onClick: () => void;
  onMouseOver: () => void;
  onMouseOut: () => void;
  currencyCode: string;
  isHovered?: boolean;
}

const PriceMarker = ({ price, name, position, onClick, onMouseOver, onMouseOut, currencyCode, isHovered }: PriceMarkerProps) => {
  const theme = useTheme();
  
  // Format currency code with /h suffix for hourly rate
  const currencyLabel = `${currencyCode}/h`;

  return (
    <Marker
      position={position}
      onClick={onClick}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      zIndex={isHovered ? 1000 : 1}
      icon={{
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="70" height="70" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="35" cy="35" r="28" fill="${isHovered ? '#FFFFFF' : theme.palette.primary.main}" stroke="${isHovered ? theme.palette.primary.main : 'none'}" stroke-width="2"/>
            <text x="35" y="40" font-family="Inter, -apple-system, BlinkMacSystemFont, system-ui, Roboto, Helvetica, Arial, sans-serif" font-size="11" font-weight="500" fill="${isHovered ? theme.palette.primary.main : '#FFFFFF'}" text-anchor="middle">${price} ${currencyLabel}</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(70, 70),
        anchor: new window.google.maps.Point(35, 35),
      }}
    />
  );
};

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '0px',
  position: 'relative' as const
};

const defaultCenter = {
  lat: 52.2297, // Warsaw fallback
  lng: 21.0122,
};

// Helper function to check if a space was created within the past 30 days
function isNewSpace(createdAt?: string): boolean {
  if (!createdAt) return false;
  
  const createdDate = new Date(createdAt);
  const now = new Date();
  const daysDiff = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24);
  
  return daysDiff <= 30;
}

const SearchPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { city: cityParam } = useParams<{ city?: string }>();
  const [selectedSpace, setSelectedSpace] = useState<SpaceSearchResult | null>(null);
  const [mapHoveredSpace, setMapHoveredSpace] = useState<SpaceSearchResult | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [, setHasSearched] = useState(false);
  const [, setMapCenter] = useState(defaultCenter);
  const [cities, setCities] = useState<string[]>(['Warszawa']);

  const { isLoaded, loadError } = useGoogleMaps();

  const [city, setCity] = useState('Warszawa');
  const [spaceNameQuery, setSpaceNameQuery] = useState('');
  const [searchBarParams, setSearchBarParams] = useState({
    date: null as Date | null,
    duration: 120,
    gears: [] as string[],
  });
  const [bookingDate, setBookingDate] = useState<Date | null>(() => {
    const now = new Date();
    // Round to the nearest 30 minutes and add 2 hours
    const minutes = Math.ceil(now.getMinutes() / 30) * 30;
    now.setMinutes(minutes);
    now.setSeconds(0);
    now.setMilliseconds(0);
    now.setHours(now.getHours() + 2);
    return now;
  });
  const [duration, setDuration] = useState<number>(120);
  const [results, setResults] = useState<SpaceSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInitiated, setSearchInitiated] = useState(false);

  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const previousPathnameRef = React.useRef<string>(location.pathname);
  const isNavigatingRef = React.useRef<boolean>(false);
  const redirectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastNavigationTimeRef = React.useRef<number>(0);
  const lastSearchEventKeyRef = React.useRef('');

  const reportSpaceSearch = useCallback((cityName: string, term: string, count: number) => {
    const key = `${cityName}|${term}|${count}`;
    if (lastSearchEventKeyRef.current === key) return;
    lastSearchEventKeyRef.current = key;
    trackSearch({
      city: cityName,
      ...(term ? { search_term: term } : {}),
      results_count: count,
    });
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Detect main content area left offset (accounts for sidebar in DashboardLayout)
  const [contentAreaLeft, setContentAreaLeft] = useState(0);
  
  useEffect(() => {
    const updateContentOffset = () => {
      // DashboardLayout creates a main content container
      const mainContent = document.querySelector('main') as HTMLElement;
      if (mainContent && window.innerWidth >= 900) {
        const rect = mainContent.getBoundingClientRect();
        setContentAreaLeft(rect.left);
      } else {
        setContentAreaLeft(0);
      }
    };
    
    updateContentOffset();
    window.addEventListener('resize', updateContentOffset);
    
    // Use ResizeObserver on main element
    const mainEl = document.querySelector('main');
    const observer = mainEl ? new ResizeObserver(updateContentOffset) : null;
    if (observer && mainEl) observer.observe(mainEl);
    
    // Also observe body for sidebar changes
    const mutationObserver = new MutationObserver(updateContentOffset);
    mutationObserver.observe(document.body, { childList: true, subtree: true, attributes: true });
    
    return () => {
      window.removeEventListener('resize', updateContentOffset);
      observer?.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [contentWidth, setContentWidth] = useState<number>(0);
  const [searchBarHeight, setSearchBarHeight] = useState<number>(0);
  const [headerHeight, setHeaderHeight] = useState<number>(64);
  const [footerHeight, setFooterHeight] = useState<number>(0);
  const searchBarRef = React.useRef<HTMLDivElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const resultsTopRef = React.useRef<HTMLDivElement | null>(null);
  
  // Gear filter state
  const [selectedGears, setSelectedGears] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  
  // Available gear types

  // Check if user is in Warsaw and redirect if no city is specified
  const [isCheckingLocation, setIsCheckingLocation] = React.useState(false);
  
  useEffect(() => {
    // Only check if no city is specified in URL and Google Maps is loaded
    if (!cityParam && isLoaded && !isCheckingLocation) {
      // Check if we've already checked location in this session
      const locationChecked = sessionStorage.getItem('warsawLocationChecked');
      
      if (!locationChecked) {
        setIsCheckingLocation(true);
        // Mark that we're checking location to avoid repeated prompts
        sessionStorage.setItem('warsawLocationChecked', 'true');
        
        // Check if user is in Warsaw
        isUserInWarsaw()
          .then((isInWarsaw) => {
            setIsCheckingLocation(false);
            if (isInWarsaw) {
              // User is in Warsaw, redirect to Warsaw search page (no query params)
              const newUrl = `/user/search/${toUrlFriendly('Warszawa')}`;
              navigate(newUrl, { replace: true });
            }
          })
          .catch((error) => {
            console.error('Error checking Warsaw location:', error);
            setIsCheckingLocation(false);
          });
      }
    }
  }, [cityParam, isLoaded, navigate, isCheckingLocation]);

  // Extract city from URL and load data
  useEffect(() => {
    // Check window.location.pathname FIRST (updates immediately when CityFilter uses replaceState)
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split('/');
    const cityIndex = pathSegments.findIndex(segment => segment === 'search');
    const cityFromPath = cityIndex !== -1 && pathSegments[cityIndex + 1] ? pathSegments[cityIndex + 1] : null;
    
    // Also check React Router's location.pathname (may lag behind)
    const locationPathSegments = location.pathname.split('/');
    const locationCityIndex = locationPathSegments.findIndex(segment => segment === 'search');
    const cityFromLocation = locationCityIndex !== -1 && locationPathSegments[locationCityIndex + 1] ? locationPathSegments[locationCityIndex + 1] : null;
    
    // Priority: cityParam (from useParams) > cityFromPath (window.location - most up-to-date) > cityFromLocation (React Router)
    // IMPORTANT: Check window.location.pathname first because CityFilter updates it directly with replaceState
    const currentCity = cityParam || cityFromPath || cityFromLocation;
    
    // If window.location.pathname has a city but React Router doesn't, wait for React Router to catch up
    // Don't redirect in this case
    const windowHasCity = !!cityFromPath;
    const routerHasCity = !!cityFromLocation || !!cityParam;
    const waitingForRouter = windowHasCity && !routerHasCity;
    
    // Check if we were on a city page BEFORE updating the ref
    const wasOnCityPage = previousPathnameRef.current && 
                         previousPathnameRef.current !== '/user/search' && 
                         previousPathnameRef.current !== '/user/search/' &&
                         previousPathnameRef.current.startsWith('/user/search/');
    
    // Check if pathname changed (indicates navigation in progress)
    const pathnameChanged = previousPathnameRef.current !== location.pathname;
    
    // If we were on a city page and now we're on /user/search, CityFilter is likely navigating
    // Set navigating flag immediately to prevent redirect
    if (wasOnCityPage && (location.pathname === '/user/search' || location.pathname === '/user/search/')) {
      isNavigatingRef.current = true;
      lastNavigationTimeRef.current = Date.now();
      // Clear the flag after navigation should complete
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 500);
    } else if (pathnameChanged) {
      // If pathname is changing, we're in the middle of a navigation - don't redirect
      isNavigatingRef.current = true;
      lastNavigationTimeRef.current = Date.now();
      // Clear the flag after navigation completes
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 300);
    }
    
    // Also check if navigation happened recently (within last 500ms)
    const timeSinceLastNavigation = Date.now() - lastNavigationTimeRef.current;
    const recentlyNavigated = timeSinceLastNavigation < 500;
    
    // Update previous pathname AFTER checking
    previousPathnameRef.current = location.pathname;
    
    // Check if CityFilter is currently navigating (using sessionStorage flag)
    const cityFilterNavigating = sessionStorage.getItem('cityFilterNavigating') === 'true';
    // Redirect to Warszawa if no city in URL, but ONLY if:
    // 1. We're not in the middle of a navigation (pathname hasn't changed recently)
    // 2. BOTH window.location AND React Router location are /user/search (not transitioning to a city)
    // 3. We weren't just on a city page (to avoid redirecting when CityFilter navigates between cities)
    // 4. No recent navigation occurred
    // 5. CityFilter is not currently navigating
    const isOnSearchPageWindow = currentPath === '/user/search' || currentPath === '/user/search/';
    const isOnSearchPageRouter = location.pathname === '/user/search' || location.pathname === '/user/search/';
    const isOnSearchPage = isOnSearchPageWindow && isOnSearchPageRouter; // Both must be true
    
    // If we were on a city page and now we're on /user/search, CityFilter might be navigating
    // Don't redirect in this case - wait for React Router to update
    // Also don't redirect if navigation happened recently or CityFilter is navigating
    // OR if window.location has a city but React Router hasn't caught up yet
    if (!currentCity && !isNavigatingRef.current && !recentlyNavigated && !cityFilterNavigating && !waitingForRouter && isOnSearchPage && !wasOnCityPage && !pathnameChanged) {
      // Clear any pending redirect timeout
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
      
      // Delay redirect to allow React Router to process CityFilter's navigation
      redirectTimeoutRef.current = setTimeout(() => {
        // Check if CityFilter is still navigating
        const stillNavigating = sessionStorage.getItem('cityFilterNavigating') === 'true';
        
        // Re-check if we still need to redirect (city might have been set by now)
        const recheckLocationSegments = location.pathname.split('/');
        const recheckLocationCityIndex = recheckLocationSegments.findIndex(segment => segment === 'search');
        const recheckCityFromLocation = recheckLocationCityIndex !== -1 && recheckLocationSegments[recheckLocationCityIndex + 1] ? recheckLocationSegments[recheckLocationCityIndex + 1] : null;
        
        const recheckPath = window.location.pathname;
        const recheckSegments = recheckPath.split('/');
        const recheckCityIndex = recheckSegments.findIndex(segment => segment === 'search');
        const recheckCityFromPath = recheckCityIndex !== -1 && recheckSegments[recheckCityIndex + 1] ? recheckSegments[recheckCityIndex + 1] : null;
        
        const recheckCity = cityParam || recheckCityFromLocation || recheckCityFromPath;
        const recheckIsOnSearchPage = location.pathname === '/user/search' || location.pathname === '/user/search/';
        
        if (!recheckCity && !stillNavigating && recheckIsOnSearchPage) {
          // Navigate without query params - filtering is handled via state
          navigate(`/user/search/${toUrlFriendly('Warszawa')}`, { replace: true });
        }
      }, 500); // 500ms delay to allow React Router to process navigation
      return;
    } else {
      // Clear redirect timeout if we have a city or if we were on a city page
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
    }
    
    // Update city state
    if (currentCity) {
      const properCityName = fromUrlFriendlyCity(currentCity, cities);
      if (city !== properCityName) {
        setCity(properCityName);
      }
    }
    
    // Load search results - use currentCity from URL
    // No longer reading filters from URL params - filtering is applied via state
    if (currentCity) {
      const loadData = async () => {
        // Use currentCity which was extracted from React Router's location.pathname
        // This ensures we use the correct city that CityFilter navigated to
        const searchCity = fromUrlFriendlyCity(currentCity, cities);
        
        setLoading(true);
        setSearchInitiated(true);
        setHasSearched(true);
        
        try {
          // Name filter is client-only (filterSpacesByNameQuery); API returns city + other filters only.
          const nameQ = spaceNameQuery.trim();
          // Initial / URL-driven loads: no slot filter so all spaces in the city are listed.
          // Date + duration apply only after the user runs a search from the bar (handleSearchParams).
          // When a name query is set, do not send gears/categories to the backend — same as name search from the bar.
          const spaces = nameQ
            ? await apiClient.searchSpaces(searchCity)
            : await apiClient.searchSpaces(
                searchCity,
                undefined,
                undefined,
                selectedGears.length > 0 ? selectedGears : undefined,
                selectedCategories.length > 0 ? selectedCategories : undefined
              );
          const filtered = filterSpacesByNameQuery(spaces, nameQ);
          setResults(filtered);
          reportSpaceSearch(searchCity, nameQ, filtered.length);
        } catch (err) {
          console.error('Failed to load spaces:', err);
        } finally {
          setLoading(false);
        }
      };
      
      loadData();
    }
  }, [cityParam, location.pathname, cities, navigate, selectedGears, selectedCategories, spaceNameQuery, reportSpaceSearch]);

  // Filtering is now applied directly via API calls, no query params needed

  useEffect(() => {
    if (!map) return;
    const spacesWithCoords = results.filter((s) => s.latitude && s.longitude);
    if (spacesWithCoords.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    spacesWithCoords.forEach((space) => {
      bounds.extend({ lat: space.latitude, lng: space.longitude });
    });

    if (spacesWithCoords.length === 1) {
      const point = { lat: spacesWithCoords[0].latitude, lng: spacesWithCoords[0].longitude };
      // Expand bounds slightly so fitBounds can compute a reasonable zoom
      // Use a tighter margin to keep a closer default zoom for a single marker
      bounds.extend({ lat: point.lat + 0.005, lng: point.lng + 0.005 });
      bounds.extend({ lat: point.lat - 0.005, lng: point.lng - 0.005 });
    }

    // Fit after a microtask so layout (especially when toggling to map on mobile) has settled
    setTimeout(() => {
      try {
        // Fit bounds so all spaces are visible on initial load and updates
        map.fitBounds(bounds, { top: 40, bottom: 60, left: 24, right: 24 });
      } catch {}
    }, 0);
  }, [map, results, viewMode, contentWidth, searchBarHeight]);

  // Scroll corresponding list card into view when a space is selected (e.g., marker hover)
  useEffect(() => {
    if (!selectedSpace) return;
    const el = document.getElementById(`space-card-${selectedSpace.id}`);
    if (el) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedSpace]);
  

  const handleAddressHover = useCallback((space: SpaceSearchResult | null) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    if (space) {
      // Immediately set the selected space
      setSelectedSpace(space);
      // Ensure the marker is visible inside the map viewport when hovering from the list
      try {
        if (map && space.latitude && space.longitude) {
          const position = { lat: space.latitude, lng: space.longitude };
          map.panTo(position);
          // Nudge up so the marker + info window aren't under the bottom/top borders
          const yNudge = window.innerWidth < 900 ? -60 : -100;
          // Defer until after panTo has applied
          setTimeout(() => {
            try { map.panBy(0, yNudge); } catch {}
          }, 0);
        }
      } catch {}
    } else {
      // Delay clearing to allow moving between hover areas
      hoverTimeoutRef.current = setTimeout(() => {
        setSelectedSpace(null);
        hoverTimeoutRef.current = null;
      }, 200);
    }
  }, [map]);
  

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchParams = async (params: {
    date: Date | null;
    duration: number;
    gears: string[];
    categories: string[];
    spaceName: string;
  }) => {
    setSearchBarParams(params);
    // Update the existing state variables to maintain compatibility
    setBookingDate(params.date);
    setDuration(params.duration);
    setSelectedGears(params.gears);
    setSelectedCategories(params.categories);
    setSpaceNameQuery(params.spaceName);
    
    // Trigger search with the new parameters
    setLoading(true);
    setSearchInitiated(true);
    setHasSearched(true);

    try {
      const currentPath = window.location.pathname;
      const pathSegments = currentPath.split('/');
      const cityIndex = pathSegments.findIndex((segment) => segment === 'search');
      const cityFromPath = cityIndex !== -1 && pathSegments[cityIndex + 1] ? pathSegments[cityIndex + 1] : null;
      const cityToUse = cityParam || cityFromPath || city;
      const searchCity = fromUrlFriendlyCity(cityToUse, cities);
      const nameQ = params.spaceName.trim();

      let spaces: SpaceSearchResult[];
      if (nameQ) {
        // Name search: backend has no name filter; load all spaces in the city and match client-side.
        // Do not send date, duration, gears, or categories — those would narrow by availability/type on the server.
        spaces = await apiClient.searchSpaces(searchCity);
      } else if (params.date) {
        const localDate = new Date(params.date.getTime() - params.date.getTimezoneOffset() * 60000);
        const isoTime = localDate.toISOString().slice(0, 19);
        spaces = await apiClient.searchSpaces(
          searchCity,
          isoTime,
          params.duration,
          params.gears.length > 0 ? params.gears : undefined,
          params.categories.length > 0 ? params.categories : undefined
        );
      } else {
        spaces = await apiClient.searchSpaces(
          searchCity,
          undefined,
          undefined,
          params.gears.length > 0 ? params.gears : undefined,
          params.categories.length > 0 ? params.categories : undefined
        );
      }

      const filtered = filterSpacesByNameQuery(spaces, nameQ);
      setResults(filtered);
      reportSpaceSearch(searchCity, nameQ, filtered.length);

      if (filtered.length > 0 && filtered[0].latitude && filtered[0].longitude) {
        setMapCenter({
          lat: filtered[0].latitude,
          lng: filtered[0].longitude,
        });

        if (map) {
          map.panTo({
            lat: filtered[0].latitude,
            lng: filtered[0].longitude,
          });
        }
      }
    } catch (err) {
      console.error('Failed to search spaces:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = () => {
    // Toggle advanced filters visibility or open filter modal
  };

  // Fetch cities on component mount
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const citiesList = await apiClient.getCities();
        // Ensure Warszawa is always in the list
        const citiesWithWarszawa = citiesList.includes('Warszawa') 
          ? citiesList 
          : ['Warszawa', ...citiesList];
        setCities(citiesWithWarszawa);
      } catch (error) {
        console.error('Failed to fetch cities:', error);
        // Keep Warszawa in the list even if API fails
        setCities(['Warszawa']);
      }
    };
    fetchCities();
  }, []);

  // Force manual scroll restoration so the browser doesn't keep the old scroll on param changes
  useEffect(() => {
    try {
      const { scrollRestoration } = window.history as History & { scrollRestoration?: string };
      const previous = scrollRestoration;
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
      return () => {
        if (previous !== undefined) {
          window.history.scrollRestoration = previous as any;
        }
      };
    } catch {}
  }, []);

  useEffect(() => {
    const updateOffsets = () => {
      if (contentRef.current) {
        const cRect = contentRef.current.getBoundingClientRect();
        setContentWidth(cRect.width);
      }
      if (searchBarRef.current) {
        const sbRect = searchBarRef.current.getBoundingClientRect();
        setSearchBarHeight(sbRect.height);
        // Position map immediately under the search bar (no extra gap)
      }
      const footerEl = document.querySelector('footer') as HTMLElement | null;
      if (footerEl) {
        setFooterHeight(footerEl.offsetHeight || 0);
      }
    };
    updateOffsets();
    window.addEventListener('resize', updateOffsets);
    return () => {
      window.removeEventListener('resize', updateOffsets);
    };
  }, []);

  // React to side menu open/close and layout shifts via ResizeObserver
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const update = () => {
      if (!contentRef.current || !searchBarRef.current) return;
      const cRect = contentRef.current.getBoundingClientRect();
      const sbRect = searchBarRef.current.getBoundingClientRect();
      setContentWidth(cRect.width);
      setSearchBarHeight(sbRect.height);
    };
    const roContent = new ResizeObserver(update);
    const roSearchBar = new ResizeObserver(update);
    const roBody = new ResizeObserver(update);
    roContent.observe(contentRef.current!);
    roSearchBar.observe(searchBarRef.current!);
    roBody.observe(document.body);
    return () => {
      roContent.disconnect();
      roSearchBar.disconnect();
      roBody.disconnect();
    };
  }, []);

  // Track footer height so the map never goes under it
  useEffect(() => {
    const footerEl = document.querySelector('footer') as HTMLElement | null;
    if (!footerEl || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      setFooterHeight(footerEl.offsetHeight || 0);
    });
    ro.observe(footerEl);
    return () => {
      ro.disconnect();
    };
  }, []);

  // Track header height so sticky search bar sits under it
  useEffect(() => {
    const headerEl = document.querySelector('.MuiAppBar-root') as HTMLElement | null;
    if (!headerEl || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      setHeaderHeight(headerEl.offsetHeight || 64);
    });
    setHeaderHeight(headerEl.offsetHeight || 64);
    ro.observe(headerEl);
    return () => {
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    const updateContentBounds = () => {
      if (contentRef.current) {
        const rect = contentRef.current.getBoundingClientRect();
        setContentWidth(rect.width);
      }
    };
    updateContentBounds();
    window.addEventListener('resize', updateContentBounds);
    return () => window.removeEventListener('resize', updateContentBounds);
  }, []);

  /** Measured toolbar height (falls back to one-row default before layout). */
  const searchBarOffset = searchBarHeight > 0 ? searchBarHeight : 64;

  const marketingCanonical =
    resolveMarketingCanonicalForAppPath(cityParam) ??
    (cityParam ? undefined : "https://band-space.com/warszawa/sale-prob/");

  return (
    <>
      <SEO 
        title="Band Space - Szukaj Sal Prób"
        description="Band Space - Szukaj sal prób, studiów nagrań i pokojów prób w Twojej okolicy. Find and book music rehearsal spaces, recording studios, and practice rooms in your city. Search for the best band practice rooms and music studios near you."
        keywords="sala prób, band space, przestrzeń muzyczna, szukaj sal prób, music rehearsal space, recording studio, practice room, band practice, music studio, rehearsal room, music venue, band practice rooms, search rehearsal spaces, studio nagrań, pokój prób, wynajem sali prób"
        url={marketingCanonical || (cityParam ? `/user/search/${cityParam}` : '/user/search')}
        canonical={marketingCanonical}
      />
      <GlobalStyles styles={{
        '.gm-ui-hover-effect': {
          display: 'none !important',
        },
        // Style Google Maps InfoWindow to match app background (removes thin white edges)
        '.gm-style .gm-style-iw, .gm-style .gm-style-iw-d, .gm-style .gm-style-iw-c': {
          background: '#1F2129 !important',
          boxShadow: 'none !important',
          border: 'none !important',
          padding: '0 !important',
          margin: '0 !important',
          borderRadius: '12px !important',
          maxWidth: 'none !important',
          overflow: 'hidden !important',
        },
        '.gm-style .gm-style-iw, .gm-style .gm-style-iw-d': {
          maxWidth: 'none !important',
          overflow: 'hidden !important',
          padding: '0 !important',
          margin: '0 !important',
        },
        '.gm-style .gm-style-iw-d::-webkit-scrollbar': {
          display: 'none !important',
        },
        '.gm-style .gm-style-iw-c': {
          padding: '0 !important',
          margin: '0 !important',
        },
        '.gm-style .gm-style-iw-c > div': {
          padding: '0 !important',
          margin: '0 !important',
        },
        '.gm-style-iw-d > div': {
          padding: '0 !important',
          margin: '0 !important',
        },
        '.gm-style-iw > div': {
          padding: '0 !important',
          margin: '0 !important',
        },
        '.gm-style-iw div:not([class*="Mui"])': {
          padding: '0 !important',
          margin: '0 !important',
        },
        '.gm-style .gm-style-iw-t, .gm-style .gm-style-iw-tc': {
          background: 'transparent !important',
          padding: '0 !important',
          margin: '0 !important',
        },
        '.gm-style .gm-style-iw-t::after, .gm-style .gm-style-iw-tc::after': {
          background: '#1F2129 !important',
          boxShadow: 'none !important',
        },
        html: {
          scrollBehavior: 'auto',
          overflowAnchor: 'none',
          overscrollBehavior: 'none',
        },
        body: {
          overflowAnchor: 'none',
          overscrollBehavior: 'none',
          position: 'fixed',
          overflow: 'hidden',
          width: '100%',
          height: '100%',
        },
        '#root': {
          overflow: 'auto',
          height: '100%',
          overscrollBehavior: 'none',
        },
      }} />
      
      {/* Overlay to cover any gap between header and search bar - positioned below the border */}
      <Box sx={{
        position: { xs: 'sticky', md: 'fixed' },
        top: { xs: 'calc(env(safe-area-inset-top, 0px) - 2px)', md: `${headerHeight - 2}px` },
        left: 0,
        right: 0,
        height: '4px',
        backgroundColor: '#1F2129',
        zIndex: 1099,
        pointerEvents: 'none'
      }} />
      
      {/* Search Bar (fixed) */}
      <Box ref={searchBarRef} sx={{
        position: { xs: 'sticky', md: 'fixed' },
        top: { 
          xs: 'env(safe-area-inset-top, 0px)', 
          md: `${headerHeight}px` 
        },
        left: { xs: 0, md: `${contentAreaLeft}px` },
        right: 0,
        zIndex: 1100,
        minHeight: { xs: 'auto', md: 64 },
        height: { xs: 'auto', md: 'auto' },
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        backgroundColor: '#1F2129',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        overflow: 'visible',
        willChange: 'transform',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}>
        <Box sx={{ width: '100%', minWidth: 0, display: 'flex', alignItems: 'stretch', px: { xs: 1, md: 2 } }}>
          <SearchBar
            onSearch={handleSearchParams}
            onFilterClick={handleFilterClick}
            disabled={loading}
            initialDate={bookingDate}
            initialDuration={duration}
            initialGears={selectedGears}
            initialCategories={selectedCategories}
            initialSpaceName={spaceNameQuery}
            rightActions={
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, val) => { if (val) setViewMode(val); }}
                size="small"
                color="primary"
              >
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
                <ToggleButton value="map">
                  <MapIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            }
          />
        </Box>
      </Box>
      
      <Box ref={contentRef} sx={{ width: '100%', mx: 'auto', mt: { xs: 0, md: 2 }, px: { xs: 0, md: 2 }, pb: { xs: 0, md: 2 } }}>
        {/* Anchor for scrolling results to top on page change */}
        <div ref={resultsTopRef} />
      </Box>

      {/* Results Section - starts immediately, content scrolls under fixed search bar */}
      <Box sx={{ width: '100%', mx: 'auto', px: 2, pt: { xs: 2, md: 0 }, mt: { xs: 0, md: `${searchBarOffset + 4}px` }, position: 'relative', zIndex: 0, minHeight: 'auto', pb: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : results.length === 0 && searchInitiated ? (
            <Typography variant="body1" align="center" mt={4}>
              {t('common.noResults', 'No spaces found.')}
            </Typography>
          ) : (
            <>
              {/* Solid overlay so content scrolls under it - only over left side (search results) */}
              <Box sx={{
                position: 'fixed',
                top: `${headerHeight + searchBarOffset}px`,
                left: { xs: 0, md: `${contentAreaLeft}px` },
                width: { 
                  xs: '100%', 
                  md: `calc((100vw - ${contentAreaLeft}px - 32px) * 0.60 + 12px)`,
                  xl: `calc((100vw - ${contentAreaLeft}px - 32px) * 0.50 + 12px)`
                },
                height: '15px',
                backgroundColor: '#1F2129',
                zIndex: 75,
                display: { xs: 'none', md: 'block' },
                pointerEvents: 'none'
              }} />
              
              <Box sx={{ 
                display: { xs: 'block', md: 'flex' },
                gap: { xs: 1.5, md: 3 },
                position: { xs: 'relative', md: 'sticky' },
                top: { xs: 0, md: `${headerHeight + searchBarOffset + 16}px` },
                zIndex: 50,
                alignItems: 'flex-start'
              }}>
                {/* Places List - Left Side */}
                <Box sx={{ 
                  width: { 
                    xs: '100%', 
                    md: 'calc(60% - 12px)',
                    xl: 'calc(50% - 12px)'
                  },
                  display: { 
                    xs: viewMode === 'list' ? 'block' : 'none',
                    md: 'block'
                  }
                }}>
                <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{
                  '& > .MuiGrid-item': {
                    paddingTop: { xs: '0 !important', md: '16px' }
                  },
                  '& > .MuiGrid-item:first-of-type': {
                    paddingTop: { xs: '0 !important', md: '0 !important' }
                  }
                }}>
                    {results.map((item) => (
                      <Grid item xs={12} sm={12} key={item.id}>
                        <Box
                          component="div"
                          id={`space-card-${item.id}`}
                          onClick={() =>
                            navigate(
                              `/user/search/${generateSpaceUrlId(item.name, item.location.toString())}`,
                              {
                                state: {
                                  roomAvailableFromTime: bookingDate?.toISOString(),
                                  durationInMinutes: duration
                                }
                              }
                            )
                          }
                          onMouseEnter={() => handleAddressHover(item)}
                          onMouseLeave={() => handleAddressHover(null)}
                          sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'stretch',
                            flexDirection: { xs: 'column', md: 'row' },
                            position: 'relative',
                            height: { xs: 'auto', md: 188 },
                            width: '100%',
                            maxWidth: '100%',
                            background: (theme) => theme.palette.background.default,
                            border: selectedSpace?.id === item.id ? '1px solid' : 'none',
                            borderColor: selectedSpace?.id === item.id ? 'primary.main' : 'transparent',
                            borderRadius: '20.08px',
                            overflow: 'hidden',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease',
                            boxShadow: selectedSpace?.id === item.id ? '0 6px 18px rgba(0,0,0,0.45)' : 'none',
                            backgroundColor: selectedSpace?.id === item.id ? '#1E213A' : undefined,
                            '&:hover': {
                              boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                              transform: 'translateY(-2px)',
                              border: '1px solid #4E67EB',
                              background: '#1E213A'
                            }
                          }}
                        >
                          {/* Image Container (Left thumbnail) */}
                          <Box sx={{ position: 'relative', flex: { xs: '0 0 auto', md: '0 0 185px' }, width: { xs: 'calc(100% - 20px)', md: 'auto' }, height: { xs: 180, md: 160 }, m: { xs: '10px', md: '14px' }, borderRadius: '9.62px', border: '0.6px solid rgba(255,255,255,0.30)', overflow: 'hidden', backgroundColor: '#0A090E' }}>
                            <MediaDisplay
                              src={item.imageUrls[0] || '/logo512.png'}
                              alt={item.name}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                zIndex: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                              }}
                            >
                              {(() => {
                                // Check both item.boosts and rankCalculationDetails.boosts
                                const boosts = item.boosts || item.rankCalculationDetails?.boosts || [];
                                console.log(`[SearchPage] Space ${item.id} (${item.name}):`, {
                                  hasBoosts: !!item.boosts,
                                  hasRankCalculationDetails: !!item.rankCalculationDetails,
                                  boostsFromDetails: item.rankCalculationDetails?.boosts,
                                  boosts,
                                  boostsCount: boosts.length,
                                  boostsData: boosts.map(b => ({
                                    id: b.id,
                                    points: b.points,
                                    type: b.type,
                                    visible: b.visible,
                                    expiresAt: b.expiresAt,
                                  }))
                                });
                                // Get today's date in UTC (without time)
                                const todayUtc = new Date();
                                todayUtc.setUTCHours(0, 0, 0, 0);
                                const visibleBoosts = boosts.filter((boost: SpaceRankBoost) => {
                                  const isVisible = boost.visible !== false;
                                  // Parse expiresAt as UTC date (without time)
                                  const expiresAt = new Date(boost.expiresAt + 'T00:00:00Z');
                                  expiresAt.setUTCHours(0, 0, 0, 0);
                                  // Show if expiresAt is NOT before today (i.e., expiresAt >= today)
                                  const isNotExpired = expiresAt >= todayUtc;
                                  console.log(`[SearchPage] Boost ${boost.id} for space ${item.id}:`, {
                                    visible: boost.visible,
                                    isVisible,
                                    expiresAt: boost.expiresAt,
                                    expiresAtDate: expiresAt,
                                    todayUtc,
                                    isBefore: expiresAt < todayUtc,
                                    isNotExpired,
                                    willShow: isVisible && isNotExpired
                                  });
                                  return isVisible && isNotExpired;
                                });
                                console.log(`[SearchPage] Space ${item.id} visible boosts:`, visibleBoosts.length, visibleBoosts);
                                return visibleBoosts.length > 0 && (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {visibleBoosts.map((boost: SpaceRankBoost, index: number) => (
                                      <Chip
                                        key={boost.id || `boost-${index}`}
                                        icon={<BoltIcon sx={{ fontSize: '0.7rem !important' }} />}
                                        label={`Boosted +${boost.points}`}
                                        size="small"
                                        color="warning"
                                        variant="filled"
                                        sx={{
                                          height: '20px',
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          '& .MuiChip-icon': {
                                            color: 'inherit',
                                          },
                                        }}
                                      />
                                    ))}
                                  </Box>
                                );
                              })()}
                              {isNewSpace(item.createdAt) && (
                                <Chip
                                  label={t('search.new', 'New Space')}
                                  size="small"
                                  sx={{
                                    height: '20px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    backgroundColor: (theme) => theme.palette.primary.main,
                                    color: '#FFFFFF',
                                    '& .MuiChip-label': {
                                      px: 1,
                                    },
                                  }}
                                />
                              )}
                              {isSpace24hOpen(item.weeklySchedule) && (
                                <Chip
                                  icon={<MusicNoteIcon sx={{ fontSize: '0.7rem !important', color: 'inherit' }} />}
                                  label={t('space.open24_7', '24/7 Open')}
                                  size="small"
                                  sx={{
                                    height: '20px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    backgroundColor: (theme) => theme.palette.success.main,
                                    color: 'white',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                    '& .MuiChip-label': {
                                      px: 1,
                                    },
                                  }}
                                />
                              )}
                            </Box>
                          </Box>

                          {/* Content Container (Right) */}
                           <Box sx={{ flex: '1 1 auto', position: 'relative', py: { xs: '10px', md: '15px' }, pr: { xs: '10px', md: '14px' }, pl: { xs: '10px', md: 0 } }}>
                            {/* Title and Reviews */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontSize: '15px',
                                  fontWeight: 700,
                                  lineHeight: '121%',
                                  letterSpacing: '0.04em',
                                  textTransform: 'uppercase',
                                  color: '#ECEBFF',
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: { xs: 'normal', md: 'nowrap' },
                                  wordBreak: 'break-word',
                                  hyphens: 'auto',
                                  display: { xs: '-webkit-box', md: 'block' },
                                  WebkitLineClamp: { xs: 2 },
                                  WebkitBoxOrient: { xs: 'vertical' }
                                }}
                              >
                                {item.name}
                              </Typography>
                            </Box>
                            {/* Availability pill (Figma Frame 113) */}
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: { xs: 0.25, md: '6.02px' },
                                px: { xs: '10px', md: '13.368px' },
                                py: { xs: '4px', md: '6.0156px' },
                                height: 'auto',
                                width: 'auto',
                                border: '0.6684px solid #8490FF',
                                borderRadius: '20.7204px',
                                color: '#FFFFFF',
                                mt: { xs: 0.25, md: 0.5 },
                                mb: { xs: 0.25, md: 0.5 },
                                maxWidth: '100%'
                              }}
                            >
                              <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, lineHeight: '120%', whiteSpace: { xs: 'normal', md: 'nowrap' }, overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textAlign: 'center' }}>
                                {t('search.roomsAvailable', { count: item.availableRoomCount })}
                              </Typography>
                              <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: { xs: '12px', md: '14.7px' }, lineHeight: '21px', letterSpacing: '0.01em', color: 'rgba(255,255,255,0.4)' }}>•</Typography>
                              <Typography sx={{ fontSize: { xs: '12px', md: '14px' }, lineHeight: '120%', whiteSpace: { xs: 'normal', md: 'nowrap' }, overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textAlign: 'center' }}>
                                {t('search.fromPerHour', { price: item.minRoomPricePerHour })}
                              </Typography>
                            </Box>

                            {/* Reviews directly under name with stars */}
                            {item.reviewStats && (
                            <Box sx={{ mb: { xs: 0.25, md: 0.5 }, height: 22, maxWidth: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Rating
                                  value={item.reviewStats.averageRating ?? 0}
                                  readOnly
                                  size="small"
                                  precision={0.1}
                                  sx={{ color: '#F3AF23' }}
                                />
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: { xs: '0.72rem', md: '0.8rem' }, 
                                    lineHeight: '22px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {`${(item.reviewStats.averageRating ?? 0).toFixed(1)} (${t('review.reviewCount', { count: item.reviewStats.reviewCount })})`}
                                </Typography>
                              </Box>
                            )}
                            {/* Location */}
                            <Box
                               display="flex" 
                               alignItems="center" 
                               gap={0.5}
                              sx={{ mb: 1, maxWidth: '100%', overflow: 'hidden', alignItems: 'flex-start' }}
                             >
                              <LocationOnIcon fontSize="small" color="action" sx={{ fontSize: '1rem' }} />
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ 
                                  fontSize: { xs: '0.72rem', md: '0.75rem' },
                                  fontWeight: 400,
                                  whiteSpace: 'normal',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  lineHeight: 1.3,
                                  maxHeight: '2.6em',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {[
                                  item.addressLine1,
                                  item.addressLine2,
                                  item.city,
                                  item.zipCode,
                                ]
                                  .filter(Boolean)
                                  .join(', ')}
                              </Typography>
                            </Box>

                            {/* Amenities - hidden on mobile to keep card compact */}
                            {item.amenities?.length > 0 && (
                              <Box sx={{ 
                                display: { xs: 'none', md: 'flex' }, 
                                flexWrap: 'wrap', 
                                gap: 0.5, 
                                mb: 1,
                                overflow: 'hidden'
                              }}>
                                {item.amenities.slice(0, 2).map((amenity) => {
                                  const key = amenity.amenityType;
                                  const label = getAmenityLabel(key) || amenity.details;
                                  return (
                                    <Typography 
                                      key={key + amenity.details}
                                      variant="body2" 
                                      color="text.secondary"
                                      sx={{ 
                                        fontSize: '0.8rem',
                                        whiteSpace: { xs: 'nowrap', md: 'normal' },
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: { xs: '50%', md: 'unset' },
                                        '&:not(:last-child)::after': {
                                          content: '" • "',
                                          color: 'text.secondary',
                                        }
                                      }}
                                    >
                                      {label}
                                    </Typography>
                                  );
                                })}
                              </Box>
                            )}
                            
                            
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  
                </Box>
                
                {/* Map - Right Side */}
                <Box sx={{ 
                  width: { 
                    xs: '100%', 
                    md: 'calc(40% - 12px)',
                    xl: 'calc(50% - 12px)'
                  },
                  display: { 
                    xs: viewMode === 'map' ? 'block' : 'none',
                    md: 'block'
                  },
                  position: { xs: 'relative', md: 'fixed' },
                  top: { xs: 0, md: `${headerHeight + searchBarOffset + 16}px` },
                  right: { xs: 0, md: '16px' },
                  height: { xs: '60vh', md: `calc(100vh - ${headerHeight + searchBarOffset + 32}px - ${footerHeight}px)` },
                  maxWidth: { 
                    xs: '100%', 
                    md: `calc((100vw - ${contentAreaLeft}px - 32px) * 0.40 - 12px)`,
                    xl: `calc((100vw - ${contentAreaLeft}px - 32px) * 0.50 - 12px)`
                  },
                  zIndex: 150
                }}>
                  {searchInitiated && results.length > 0 && (
                    <Box sx={{
                      width: '100%',
                      height: '100%',
                      zIndex: 0
                    }}>
                      <Paper 
                        elevation={3} 
                        sx={{ 
                          height: '100%', 
                          overflow: 'hidden', 
                          borderRadius: 2,
                          position: 'relative'
                        }}
                      >
                      {loadError && (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <Typography color="error">{t('common.errorLoadingMaps', 'Error loading maps')}</Typography>
                        </Box>
                      )}
                      {!isLoaded ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <CircularProgress />
                        </Box>
                      ) : (
                        <GoogleMap
                          mapContainerStyle={mapContainerStyle}
                          onLoad={setMap}
                          options={{
                            gestureHandling: 'cooperative',
                            scrollwheel: false,
                            fullscreenControl: false,
                            streetViewControl: false,
                            mapTypeControl: false,
                            zoomControl: true,
                            clickableIcons: false,
                          }}
                        >
                          {results.map((space) => (
                            space.latitude && space.longitude && (
                              <PriceMarker
                                key={space.id}
                                position={{
                                  lat: space.latitude,
                                  lng: space.longitude,
                                }}
                                price={Math.round(space.minRoomPricePerHour).toString()}
                                name={space.name}
                                onClick={() => {
                                  if (isMobile) {
                                    // On mobile: clicking marker only shows info window
                                    setSelectedSpace(space);
                                    setMapHoveredSpace(null);
                                  } else {
                                    // Desktop: navigate immediately
                                    navigate(
                                      `/user/search/${generateSpaceUrlId(space.name, space.location.toString())}`,
                                      {
                                        state: {
                                          roomAvailableFromTime: bookingDate?.toISOString(),
                                          durationInMinutes: duration
                                        }
                                      }
                                    );
                                  }
                                }}
                                onMouseOver={() => setMapHoveredSpace(space)}
                                onMouseOut={() => setMapHoveredSpace(null)}
                                currencyCode={space.currencyCode ? space.currencyCode.toString() : 'PLN'}
                                isHovered={(selectedSpace?.id === space.id) || (mapHoveredSpace?.id === space.id)}
                              />
                            )
                          ))}
                          {(mapHoveredSpace || selectedSpace) && (mapHoveredSpace?.latitude || selectedSpace?.latitude) && (mapHoveredSpace?.longitude || selectedSpace?.longitude) && (
                            // Drive InfoWindow from map hover (preferred) or selected from list
                            <InfoWindow
                              position={(mapHoveredSpace ? {
                                lat: mapHoveredSpace.latitude!,
                                lng: mapHoveredSpace.longitude!,
                              } : {
                                lat: selectedSpace!.latitude!,
                                lng: selectedSpace!.longitude!,
                              })}
                              options={{
                                disableAutoPan: false,
                                maxWidth: 264,
                                pixelOffset: new window.google.maps.Size(0, -30),
                              }}
                              onCloseClick={() => {
                                setSelectedSpace(null);
                                setMapHoveredSpace(null);
                              }}
                            >
                              <Box
                                component="div"
                                sx={{
                                  pt: 0.85,
                                  px: 1.5,
                                  pb: 1.5,
                                  bgcolor: 'background.paper',
                                  borderRadius: 1,
                                  color: 'text.primary',
                                  minWidth: 192,
                                  maxWidth: 240,
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  boxShadow: 3,
                                  '& .gm-ui-hover-effect': {
                                    display: 'none',
                                  },
                                }}
                              onClick={() => {
                                const space = mapHoveredSpace || selectedSpace;
                                if (space) {
                                  // Navigate to space page when info window is clicked
                                  navigate(
                                    `/user/search/${generateSpaceUrlId(space.name, space.location.toString())}`,
                                    {
                                      state: {
                                        roomAvailableFromTime: bookingDate?.toISOString(),
                                        durationInMinutes: duration
                                      }
                                    }
                                  );
                                }
                              }}
                                onMouseEnter={() => {
                                  if (mapHoveredSpace) setMapHoveredSpace(mapHoveredSpace);
                                }}
                                onMouseLeave={() => setMapHoveredSpace(null)}
                              >
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{ 
                                    fontWeight: 700,
                                    mb: 0.75,
                                    mt: 0,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {(mapHoveredSpace || selectedSpace)!.name}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mb: 0.5
                                  }}
                                >
                                  <Typography sx={{ fontSize: '0.7rem' }}>
                                    {t('search.roomsAvailable', { count: (mapHoveredSpace || selectedSpace)!.availableRoomCount })}
                                  </Typography>
                                  <Typography sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>•</Typography>
                                  <Typography sx={{ fontSize: '0.7rem' }}>
                                    {t('search.fromPerHour', { price: (mapHoveredSpace || selectedSpace)!.minRoomPricePerHour })}
                                  </Typography>
                                </Box>
                                <Divider sx={{ my: 0.75 }} />
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <LocationOnIcon fontSize="small" color="action" sx={{ fontSize: '0.9rem' }} />
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary" 
                                    sx={{ fontSize: '0.7rem', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3 }}
                                  >
                                    {[(mapHoveredSpace || selectedSpace)!.addressLine1, (mapHoveredSpace || selectedSpace)!.addressLine2, (mapHoveredSpace || selectedSpace)!.city, (mapHoveredSpace || selectedSpace)!.zipCode]
                                      .filter(Boolean)
                                      .join(', ')}
                                  </Typography>
                                </Box>
                              </Box>
                            </InfoWindow>
                          )}
                        </GoogleMap>
                      )}
                      </Paper>
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
      </Box>
    </>
  );
};

export default SearchPage;